import type { EntityRef } from "@/types/maxwell";

/** Narrow one loose JSON value to the `EntityRef` citation shape. */
export function isEntityRef(r: unknown): r is EntityRef {
  return (
    typeof r === "object" &&
    r !== null &&
    ((r as EntityRef).kind === "deal" ||
      (r as EntityRef).kind === "customer" ||
      (r as EntityRef).kind === "contact" ||
      (r as EntityRef).kind === "agent_run") &&
    typeof (r as EntityRef).id === "string" &&
    typeof (r as EntityRef).label === "string"
  );
}

/** Cycle-free backstop for the recursive walk — result payloads are small trees. */
const MAX_DEPTH = 8;

/**
 * Collect every `entity_refs` citation anywhere in a step-result JSON payload
 * (top level or nested inside result rows — e.g. `detect_expiring_contracts`
 * nests refs per expiring item), deduplicated by `(kind, id)` in first-seen
 * order. Mirrors the server-side walk in `autonomy/summary.rs`.
 */
export function collectEntityRefs(value: unknown): EntityRef[] {
  const out: EntityRef[] = [];
  const seen = new Set<string>();
  walk(value, out, seen, 0);
  return out;
}

function walk(value: unknown, out: EntityRef[], seen: Set<string>, depth: number): void {
  if (depth > MAX_DEPTH || typeof value !== "object" || value === null) return;
  if (Array.isArray(value)) {
    for (const item of value) walk(item, out, seen, depth + 1);
    return;
  }
  for (const [key, child] of Object.entries(value)) {
    if (key === "entity_refs" && Array.isArray(child)) {
      for (const ref of child) {
        if (!isEntityRef(ref)) continue;
        const dedup = `${ref.kind}:${ref.id}`;
        if (!seen.has(dedup)) {
          seen.add(dedup);
          out.push(ref);
        }
      }
    } else {
      walk(child, out, seen, depth + 1);
    }
  }
}
